import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Header, Footer } from "../components/layout";
import { AppDownloadBanner } from "../components/sections/AppDownloadBanner";
import { SEO } from "../components/SEO";
import { Container } from "../components/ui/Container";
import { fetchPublicOffers, PublicOfferItem } from "../apis/offers";
import { fetchPopularShops, PopularShopItem } from "../apis/shops";
import { fetchExploreCategories, ExploreCategoryItem } from "../apis/categories";
import { fetchSpotlights, SpotlightsResponse, fallbackSpotlights } from "../apis/spotlights";
import { getSmartAppDownloadUrl } from "../utils/appLinks";
import {
  CITY_COORDINATES,
  POPULAR_LOCATIONS,
  DEFAULT_COORDINATES,
} from "../utils/locations";

// ── Data Interfaces ───────────────────────────────────────────────────────────
interface DisplayShop {
  id: string;
  name: string;
  location: string;
  image: string;
  category: string;
  avatarBg: string;
  avatarLetter: string;
  slug: string;
  rating: string | number;
  offersCount?: string;
  distance?: string | null;
}

interface DisplayDeal {
  id: string;
  title: string;
  badge: string;
  badgeType: "green" | "teal";
  merchant: string;
  merchantAvatar: string;
  image: string;
  category: string;
  location: string;
  discount: string;
  distance?: string | null;
}

// ── Rich Category Icon Component ──────────────────────────────────────────────
const CategoryIcon: React.FC<{ type: string; iconUrl?: string | null; className?: string }> = ({
  type,
  iconUrl,
  className = "w-5 h-5",
}) => {
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className={`${className} object-contain`}
        onError={(e) => {
          (e.currentTarget as HTMLElement).style.display = "none";
        }}
      />
    );
  }

  const normalized = (type || "").toLowerCase();

  switch (normalized) {
    case "all":
    case "cube":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      );
    case "dining":
    case "restaurant":
    case "food":
    case "bakery":
    case "cupcake":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a3 3 0 00-3 3c0 .34.06.66.16.96A4 4 0 005 9.5a4 4 0 00.5 1.95A4 4 0 007 14h10a4 4 0 001.5-2.55A4 4 0 0019 9.5a4 4 0 00-4.16-3.54c.1-.3.16-.62.16-.96a3 3 0 00-3-3zM7 14l1.5 8h7l1.5-8" />
        </svg>
      );
    case "lifestyle":
    case "fashion":
    case "clothing":
    case "watch":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 2h6M9 22h6" />
        </svg>
      );
    case "beauty":
    case "salon":
    case "spa":
    case "sparkles":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.286L13 21l-2.286-6.857L5 12l5.714-2.286L13 3z" />
        </svg>
      );
    case "services":
    case "construction":
    case "repair":
    case "wrench":
    case "ruler":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      );
    case "car":
    case "automobile":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 17h8m-8 0a2 2 0 11-4 0 2 2 0 014 0zm8 0a2 2 0 114 0 2 2 0 01-4 0zM5 11l2-5h10l2 5M5 11h14v5H5v-5z" />
        </svg>
      );
    case "academic":
    case "education":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
        </svg>
      );
    case "accommodation":
    case "hotel":
    case "bed":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M5 18v2M19 18v2M5 6v4m0 0h14V6" />
        </svg>
      );
    case "grocery":
    case "mart":
    case "cart":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    case "paw":
    case "animal":
    case "pets":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 14c-2.2 0-4 1.8-4 4s1.8 4 4 4 4-1.8 4-4-1.8-4-4-4zm-5-3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-8-5c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
        </svg>
      );
    case "health":
    case "hospital":
    case "clinic":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      );
    default:
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      );
  }
};

export default function DealsPage() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "all";
  const initialLocation = searchParams.get("location") || "All";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(
    CITY_COORDINATES[initialLocation] || DEFAULT_COORDINATES
  );
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [selectedDealForModal, setSelectedDealForModal] = useState<DisplayDeal | null>(null);

  // ── Backend Live States ─────────────────────────────────────────────────────
  const [categories, setCategories] = useState<ExploreCategoryItem[]>([]);
  const [spotlights, setSpotlights] = useState<SpotlightsResponse>(fallbackSpotlights);
  const [backendShops, setBackendShops] = useState<PopularShopItem[]>([]);
  const [backendOffers, setBackendOffers] = useState<PublicOfferItem[]>([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [loadingDeals, setLoadingDeals] = useState(false);

  const selectedCategoryName = useMemo(() => {
    if (selectedCategory === "all") return "";
    return categories.find((c) => c.slug === selectedCategory || c.id === selectedCategory)?.name || selectedCategory;
  }, [selectedCategory, categories]);

  const locationDropdownRef = useRef<HTMLDivElement>(null);

  // ── 1. Fetch Categories from Backend ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    fetchExploreCategories().then((cats) => {
      if (!cancelled) {
        setCategories(cats || []);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── 2. Fetch Spotlight Banners from Backend (Restaurants & Services) ──────────
  useEffect(() => {
    let cancelled = false;
    fetchSpotlights({
      location: selectedLocation,
      lat: userCoords?.lat,
      lng: userCoords?.lng,
    }).then((data) => {
      if (!cancelled && data) {
        setSpotlights(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedLocation, userCoords]);

  // ── 3. Fetch Featured Shops from Backend (Dynamic with category & search) ─────
  useEffect(() => {
    let cancelled = false;
    setLoadingShops(true);

    const categoryParam =
      selectedCategory !== "all"
        ? categories.find((c) => c.slug === selectedCategory || c.id === selectedCategory)?.name ||
          selectedCategory
        : undefined;

    fetchPopularShops({
      limit: 8,
      category: categoryParam,
      search: searchQuery,
      location: selectedLocation,
      lat: userCoords?.lat,
      lng: userCoords?.lng,
    })
      .then((shops) => {
        if (!cancelled) {
          setBackendShops(shops);
        }
      })
      .catch((err) => {
        console.warn("fetchPopularShops error:", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingShops(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCategory, searchQuery, selectedLocation, userCoords, categories]);

  // ── 4. Fetch Featured Deals from Backend (Dynamic with category & search) ──────
  useEffect(() => {
    let cancelled = false;
    setLoadingDeals(true);

    const categoryParam =
      selectedCategory !== "all"
        ? categories.find((c) => c.slug === selectedCategory || c.id === selectedCategory)?.name ||
          selectedCategory
        : undefined;

    fetchPublicOffers({
      limit: 12,
      category: categoryParam,
      search: searchQuery,
      lat: userCoords?.lat,
      lng: userCoords?.lng,
    })
      .then((res) => {
        if (!cancelled && res.offers) {
          setBackendOffers(res.offers);
        }
      })
      .catch((err) => {
        console.warn("fetchPublicOffers error:", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingDeals(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCategory, searchQuery, userCoords, categories]);

  // ── Handle GPS Location Detection ──────────────────────────────────────────
  const handleDetectGPS = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSelectedLocation("Current Location");
        setUserCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setIsLocationDropdownOpen(false);
      },
      () => {
        alert("Location access denied or unavailable.");
      }
    );
  };

  // Close location dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target as Node)) {
        setIsLocationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Map Backend Shops for Display ───────────────────────────────────────────
  const displayShops: DisplayShop[] = useMemo(() => {
    const colors = [
      "bg-emerald-500 text-white",
      "bg-amber-100 text-amber-800",
      "bg-stone-900 text-white",
      "bg-rose-400 text-white",
      "bg-purple-600 text-white",
      "bg-indigo-600 text-white",
      "bg-cyan-600 text-white",
    ];

    return backendShops.map((b, idx) => {
      const avatarLetters = (b.name || "Shop")
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

      return {
        id: b.id,
        name: b.name,
        location: b.location || (selectedLocation === "All" ? "Kerala" : `${selectedLocation}, Kerala`),
        image: b.image || "/deals/shop_vibe.jpg",
        category: b.category,
        avatarBg: colors[idx % colors.length],
        avatarLetter: avatarLetters || "S",
        slug: b.slug || b.id,
        rating: Number(b.rating) > 0 ? b.rating : 0,
        offersCount: b.offersCount,
        distance: b.distance,
      };
    });
  }, [backendShops, selectedLocation]);

  // ── Map Backend Deals for Display ───────────────────────────────────────────
  const displayDeals: DisplayDeal[] = useMemo(() => {
    const avatars = ["🧁", "✨", "🍞", "💇", "🛒", "🍹", "🛍️", "🏷️", "🍣", "☕"];

    return backendOffers.map((o, idx) => {
      const badge = o.badge || "SPECIAL DEAL";
      const isGreen = badge.includes("BUY 1 GET 1") || badge.includes("₹");
      return {
        id: o.id,
        title: o.title,
        badge,
        badgeType: isGreen ? "green" : "teal",
        merchant: o.merchant,
        merchantAvatar: avatars[idx % avatars.length],
        image: o.image || "/deals/deal_fresh_muffins.jpg",
        category: o.category || "Deal",
        location: o.distance ? `${o.distance} away` : (selectedLocation === "All" ? "Kerala" : `${selectedLocation}, Kerala`),
        discount: o.description || `${badge} available in-store`,
        distance: o.distance,
      };
    });
  }, [backendOffers, selectedLocation]);

  return (
    <div className="min-h-screen bg-[#FAFAFC] text-slate-800 flex flex-col font-sans">
      <SEO
        title="SetGo Deals Near You & Rewards | Unlock Instant Store Discounts"
        description="Discover exclusive deals, buy-1-get-1 offers, restaurants and popular partner shops near Ernakulam & Kochi. Earn loyalty coins with every redemption at the billing counter."
        url="/deals"
      />

      {/* Header */}
      <Header variant="solid" />

      <main className="flex-1 pt-20">
        {/* ── SECTION 1: SEARCH & LOCATION BAR ─────────────────────────────────────── */}
        <section className="bg-[#ECE7F6] py-5 px-4 sm:px-6 border-b border-purple-200/50">
          <Container size="xl" padding="none">
            <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-2 sm:p-2.5 flex flex-col md:flex-row items-center gap-2 md:gap-3 transition-shadow hover:shadow-md">
              {/* Location Selector Dropdown */}
              <div className="relative w-full md:w-auto" ref={locationDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                  className="w-full md:w-auto flex items-center justify-between md:justify-start gap-2.5 px-3.5 py-2 rounded-xl hover:bg-purple-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 text-purple-700">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div className="leading-tight">
                      <div className="text-[10px] uppercase font-semibold text-purple-600 tracking-wider">
                        {selectedLocation === "All" ? "Area" : "Deals near"}
                      </div>
                      <div className="text-sm font-bold text-slate-900 flex items-center gap-1">
                        {selectedLocation === "All" ? "All Locations" : selectedLocation}
                        <svg
                          className={`w-3.5 h-3.5 text-purple-600 transition-transform ${isLocationDropdownOpen ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {isLocationDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-2 w-full sm:w-64 max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-xl border border-purple-100 py-2 z-50 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={handleDetectGPS}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors border-b border-purple-100"
                      >
                        <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" strokeWidth="2" />
                          <circle cx="12" cy="12" r="4" fill="currentColor" />
                        </svg>
                        Use Current GPS Location
                      </button>
                      {/* "All" Option */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLocation("All");
                          setUserCoords(DEFAULT_COORDINATES);
                          setIsLocationDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-xs font-bold flex items-center justify-between transition-colors border-b border-purple-100 ${
                          selectedLocation === "All"
                            ? "text-purple-700 bg-purple-50 font-bold"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <span>All Locations (Distance sorted)</span>
                        {selectedLocation === "All" && (
                          <svg className="w-3.5 h-3.5 text-purple-700" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Districts (Kerala)
                      </div>
                      <div className="max-h-56 overflow-y-auto">
                        {POPULAR_LOCATIONS.map((loc) => (
                          <button
                            key={loc}
                            type="button"
                            onClick={() => {
                              setSelectedLocation(loc);
                              if (CITY_COORDINATES[loc]) {
                                setUserCoords(CITY_COORDINATES[loc]);
                              }
                              setIsLocationDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-xs font-medium flex items-center justify-between transition-colors ${
                              selectedLocation === loc
                                ? "text-purple-700 bg-purple-50 font-bold"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            <span>{loc}</span>
                            {selectedLocation === loc && (
                              <svg className="w-3.5 h-3.5 text-purple-700" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Vertical Divider */}
              <div className="hidden md:block w-px h-8 bg-slate-200" />

              {/* Search Input */}
              <div className="flex-1 w-full flex items-center gap-2 px-2">
                <svg className="w-5 h-5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for stores, salons, cafés, offers"
                  className="w-full text-sm text-slate-900 placeholder:text-slate-400 bg-transparent focus:outline-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="p-1 text-slate-400 hover:text-slate-600 rounded-full"
                    title="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Search Action / Status */}
              <button
                type="button"
                className="w-full md:w-auto px-7 py-2.5 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-sm shadow-sm transition-all hover:shadow active:scale-95 flex items-center justify-center gap-2"
              >
                {(loadingShops || loadingDeals) && (
                  <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Search
              </button>
            </div>
          </Container>
        </section>

        {/* ── SECTION 2: EXPLORE CATEGORIES (API Powered) ───────────────────────────── */}
        <section className="py-8 bg-white border-b border-slate-100">
          <Container size="xl" padding="md">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-extrabold text-slate-900">Explore Categories</h2>
              <span className="text-xs text-slate-500 font-medium">
                {categories.length} categories available
              </span>
            </div>
            <div className="flex items-start gap-3 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-purple-200 scrollbar-track-transparent">
              {categories.map((cat) => {
                const isActive = selectedCategory === cat.slug || (cat.id === "all" && selectedCategory === "all");
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.slug || cat.id)}
                    className={`flex-shrink-0 flex flex-col items-center justify-center p-3 rounded-2xl transition-all duration-200 text-center w-28 h-28 relative ${
                      isActive
                        ? "bg-purple-50 border-2 border-purple-600 text-purple-700 shadow-sm"
                        : "bg-slate-50/70 hover:bg-slate-100/90 text-slate-600 border border-slate-200/60"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 transition-transform ${
                        isActive ? "bg-purple-600 text-white scale-110 shadow-sm" : "bg-white text-purple-600 shadow-xs"
                      }`}
                    >
                      <CategoryIcon type={cat.iconType} iconUrl={cat.iconUrl} className="w-5 h-5" />
                    </div>
                    <span className="text-[11px] font-semibold leading-tight line-clamp-2 px-1">
                      {cat.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </Container>
        </section>

        {/* ── SECTION 3: SPOTLIGHT FEATURED BANNERS (API Powered) ──────────────────── */}
        <section className="py-8 bg-[#FAFAFC]">
          <Container size="xl" padding="md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Banner 1: Restaurants */}
              <motion.div
                whileHover={{ y: -3 }}
                transition={{ duration: 0.2 }}
                className="rounded-3xl p-5 sm:p-8 bg-[#FFF0E2] flex flex-col-reverse sm:flex-row items-center justify-between overflow-hidden shadow-xs border border-amber-100 relative group gap-4"
              >
                <div className="space-y-3 z-10 w-full sm:max-w-[55%] text-left">
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-black text-slate-950 tracking-tight">
                    {spotlights.restaurants.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 font-medium">
                    {spotlights.restaurants.count > 0
                      ? selectedLocation === "All"
                        ? `${spotlights.restaurants.count}+ partner shops across Kerala`
                        : `${spotlights.restaurants.count}+ shops near ${selectedLocation}`
                      : spotlights.restaurants.subtitle}
                  </p>
                  <div>
                    <Link
                      to="/shops?category=restaurants"
                      className="inline-flex items-center px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold text-white bg-[#C2410C] hover:bg-[#9A3412] shadow-sm transition-all active:scale-95"
                    >
                      {spotlights.restaurants.ctaText || "Explore shops"}
                    </Link>
                  </div>
                </div>

                {/* Right Circular Platter */}
                <div className="relative w-32 h-32 sm:w-44 sm:h-44 md:w-48 md:h-48 flex-shrink-0 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-amber-500/10 blur-xl group-hover:scale-110 transition-transform" />
                  <img
                    src={spotlights.restaurants.image || "/deals/spotlight_food_platter.jpg"}
                    alt="Gourmet restaurant food platter"
                    className="w-full h-full object-cover rounded-full shadow-lg border-2 border-white/80 group-hover:rotate-3 transition-transform duration-500"
                  />
                </div>
              </motion.div>

              {/* Banner 2: Book Trusted Services Fast */}
              <motion.div
                whileHover={{ y: -3 }}
                transition={{ duration: 0.2 }}
                className="rounded-3xl p-5 sm:p-8 bg-[#E0F3F5] flex flex-col-reverse sm:flex-row items-center justify-between overflow-hidden shadow-xs border border-cyan-100 relative group gap-4"
              >
                <div className="space-y-3 z-10 w-full sm:max-w-[55%] text-left">
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-black text-slate-950 tracking-tight leading-tight">
                    {spotlights.services.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-cyan-950 font-medium leading-relaxed">
                    {spotlights.services.subtitle}
                  </p>
                  <div>
                    <Link
                      to="/services"
                      className="inline-flex items-center px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold text-white bg-[#0E7490] hover:bg-[#155E75] shadow-sm transition-all active:scale-95"
                    >
                      {spotlights.services.ctaText || "Explore services"}
                    </Link>
                  </div>
                </div>

                {/* Right Interior Photo */}
                <div className="relative w-32 h-32 sm:w-44 sm:h-44 md:w-48 md:h-48 flex-shrink-0 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-2xl bg-cyan-500/10 blur-xl group-hover:scale-110 transition-transform" />
                  <img
                    src={spotlights.services.image || "/deals/spotlight_home_service.jpg"}
                    alt="Trusted home service partner"
                    className="w-full h-full object-cover rounded-2xl shadow-lg border-2 border-white/80 group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </motion.div>
            </div>
          </Container>
        </section>

        {/* ── SECTION 4: FEATURED SHOPS (API Powered) ──────────────────────────────── */}
        <section className="py-8 bg-white border-t border-slate-100">
          <Container size="xl" padding="md">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <div>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                  {selectedCategory !== "all" ? `${selectedCategoryName} Shops` : "Featured Shops"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selectedCategory !== "all"
                    ? `Partner stores in ${selectedCategoryName} ${selectedLocation === "All" ? "across Kerala" : `near ${selectedLocation}`}`
                    : selectedLocation === "All"
                    ? "Top-rated partner stores across Kerala"
                    : `Top-rated partner stores in and around ${selectedLocation}`}
                </p>
              </div>
              <Link
                to={selectedCategory !== "all" ? `/shops?category=${selectedCategory}` : "/shops"}
                className="self-start sm:self-auto text-sm font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors group"
              >
                View all shops <span className="group-hover:translate-x-0.5 transition-transform">›</span>
              </Link>
            </div>

            {loadingShops && displayShops.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-slate-100 rounded-2xl h-60 animate-pulse" />
                ))}
              </div>
            ) : displayShops.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                <p className="text-slate-500 text-sm font-medium">
                  {selectedCategory !== "all"
                    ? `No partner shops found in ${selectedCategoryName}.`
                    : "No shops found matching your search."}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategory("all");
                    setSearchQuery("");
                  }}
                  className="mt-2 text-xs font-bold text-purple-600 hover:underline"
                >
                  Reset filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {displayShops.slice(0, 4).map((shop) => (
                  <Link
                    key={shop.id}
                    to={`/businesses/${shop.slug}`}
                    className="block group"
                  >
                    <motion.div
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.2 }}
                      className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full"
                    >
                      {/* Shop Banner Image */}
                      <div className="relative h-44 w-full overflow-hidden bg-slate-100">
                        <img
                          src={shop.image}
                          alt={shop.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {Number(shop.rating) > 0 && (
                          <div className="absolute top-2.5 right-2.5 bg-black/50 backdrop-blur-md text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <svg className="w-3 h-3 text-amber-400 fill-current" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            {shop.rating}
                          </div>
                        )}
                        {shop.distance && (
                          <div className="absolute bottom-2.5 left-2.5 bg-white/90 backdrop-blur-md text-slate-800 text-[10px] font-extrabold px-2 py-0.5 rounded-md shadow-xs">
                            📍 {shop.distance}
                          </div>
                        )}
                      </div>

                      {/* Shop Details */}
                      <div className="p-4 flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shadow-xs flex-shrink-0 ${shop.avatarBg}`}
                        >
                          {shop.avatarLetter}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-extrabold text-slate-900 text-sm truncate group-hover:text-purple-700 transition-colors">
                            {shop.name}
                          </h4>
                          <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                            <span>📍</span> {shop.location}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            )}
          </Container>
        </section>

        {/* ── SECTION 5: FEATURED DEALS (API Powered 12 Cards Grid) ───────────────── */}
        <section className="py-10 bg-[#FAFAFC] border-t border-slate-100">
          <Container size="xl" padding="md">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <div>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                  {selectedCategory !== "all" ? `${selectedCategoryName} Deals` : "Featured Deals"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selectedCategory !== "all"
                    ? `Exclusive promotions and vouchers for ${selectedCategoryName}`
                    : "Redeem instantly at checkout and earn loyalty rewards"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={selectedCategory !== "all" ? `/offers?category=${selectedCategory}` : "/offers"}
                  className="text-sm font-bold text-purple-600 hover:text-purple-700 flex items-center gap-1 transition-colors group"
                >
                  View all deals & services <span className="group-hover:translate-x-0.5 transition-transform">›</span>
                </Link>
                <div className="text-xs font-semibold px-3 py-1 bg-purple-100 text-purple-700 rounded-full">
                  {displayDeals.length} active deals
                </div>
              </div>
            </div>

            {loadingDeals && displayDeals.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="bg-slate-100 rounded-2xl h-64 animate-pulse" />
                ))}
              </div>
            ) : displayDeals.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200 p-6 max-w-lg mx-auto">
                <p className="text-slate-600 font-semibold text-sm">
                  {selectedCategory !== "all"
                    ? `No active deals available in ${selectedCategoryName} right now.`
                    : "No deals match your search."}
                </p>
                {selectedCategory !== "all" && displayShops.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Check out the partner shops in {selectedCategoryName} listed above!
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategory("all");
                    setSearchQuery("");
                  }}
                  className="mt-4 px-4 py-2 rounded-xl text-xs font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors"
                >
                  Reset filters & view all deals
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {displayDeals.map((deal) => (
                  <motion.div
                    key={deal.id}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setSelectedDealForModal(deal)}
                    className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-md transition-shadow group cursor-pointer flex flex-col"
                  >
                    {/* Deal Image with Discount Badge */}
                    <div className="relative h-44 w-full overflow-hidden bg-slate-100">
                      <img
                        src={deal.image}
                        alt={deal.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Top-Left Discount Badge */}
                      <div className="absolute top-2.5 left-2.5">
                        <span
                          className={`inline-block text-[10px] font-black tracking-wider text-white px-2.5 py-1 rounded-md shadow-sm uppercase ${
                            deal.badgeType === "green" ? "bg-[#059669]" : "bg-[#0D9488]"
                          }`}
                        >
                          {deal.badge}
                        </span>
                      </div>
                      {deal.distance && (
                        <div className="absolute bottom-2.5 right-2.5 bg-black/60 backdrop-blur-md text-white text-[10px] font-semibold px-2 py-0.5 rounded-md">
                          {deal.distance}
                        </div>
                      )}
                    </div>

                    {/* Deal Meta */}
                    <div className="p-4 space-y-1.5 flex-1 flex flex-col justify-between">
                      <div>
                        <h4 className="font-extrabold text-slate-900 text-sm group-hover:text-purple-700 transition-colors">
                          {deal.title}
                        </h4>
                        <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                          {deal.discount}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-slate-50 mt-2">
                        <div className="w-5 h-5 rounded-full bg-purple-50 text-purple-700 flex items-center justify-center text-xs flex-shrink-0 font-bold">
                          {deal.merchantAvatar}
                        </div>
                        <span className="text-xs font-semibold text-slate-700 truncate">
                          {deal.merchant}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </Container>
        </section>

        {/* ── SECTION 6: APP DOWNLOAD BANNER ───────────────────────────────────────── */}
        <AppDownloadBanner description="The app shows the offers around you wherever you are, and lets you verify each redemption with the merchant's OTP at the counter." />
      </main>

      {/* Footer */}
      <Footer />

      {/* ── DEAL DETAIL / REDEMPTION MODAL ───────────────────────────────────────── */}
      <AnimatePresence>
        {selectedDealForModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl max-w-lg w-full max-h-[92vh] overflow-y-auto my-auto shadow-2xl border border-purple-100"
            >
              {/* Modal Banner */}
              <div className="relative h-52 w-full">
                <img
                  src={selectedDealForModal.image}
                  alt={selectedDealForModal.title}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => setSelectedDealForModal(null)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black transition-colors"
                >
                  ✕
                </button>
                <div className="absolute top-3 left-3">
                  <span className="inline-block text-xs font-black tracking-wider text-white px-3 py-1 rounded-md shadow-sm uppercase bg-[#059669]">
                    {selectedDealForModal.badge}
                  </span>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-black text-slate-950">
                    {selectedDealForModal.title}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {selectedDealForModal.discount}
                  </p>
                </div>

                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-2xl border border-purple-100">
                  <div className="w-10 h-10 rounded-xl bg-purple-200 flex items-center justify-center text-lg">
                    {selectedDealForModal.merchantAvatar}
                  </div>
                  <div>
                    <div className="text-xs uppercase font-bold text-purple-700">Offered by</div>
                    <div className="text-sm font-extrabold text-slate-900">{selectedDealForModal.merchant}</div>
                    <div className="text-[11px] text-slate-500">📍 {selectedDealForModal.location}</div>
                  </div>
                </div>

                {/* Steps to Redeem */}
                <div className="space-y-2 pt-2">
                  <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    How to redeem in-store
                  </h5>
                  <ol className="text-xs text-slate-600 space-y-1.5 list-decimal list-inside bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                    <li>Visit the partner store at {selectedDealForModal.location}.</li>
                    <li>Open your SetGo Customer App and navigate to "My Deals".</li>
                    <li>Present the 4-digit OTP to the merchant at checkout.</li>
                    <li>Enjoy instant discount & earn bonus SetGo loyalty coins!</li>
                  </ol>
                </div>

                {/* Actions */}
                <div className="pt-2 flex flex-wrap items-center gap-2.5 sm:gap-3">
                  <a
                    href={getSmartAppDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      setSelectedDealForModal(null);
                    }}
                    className="flex-1 min-w-[130px] py-3 px-4 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-sm text-center shadow-sm transition-all"
                  >
                    Open in App
                  </a>
                  <Link
                    to={`/offers/${selectedDealForModal.id || "premium-haircut-offer"}`}
                    className="py-3 px-4 rounded-xl border border-purple-200 text-purple-700 hover:bg-purple-50 font-bold text-sm text-center transition-colors"
                  >
                    View Offer Page
                  </Link>
                  <button
                    type="button"
                    onClick={() => setSelectedDealForModal(null)}
                    className="py-3 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
